import React, { useEffect, useState } from 'react';
import { AlertCircleIcon, InfoIcon, CheckCircleIcon, XIcon, XCircleIcon, ClockIcon, BrainCircuitIcon, MessageSquareIcon, MicIcon, ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
import { useClosingAssistant } from '../../contexts/ClosingAssistantContext';
import { useRecording } from '../../contexts/RecordingContext';
import { useChat } from '../../contexts/ChatContext';
import { useAISalesAssistant } from '../../contexts/AISalesAssistantContext';
export const InsightsTab: React.FC = () => {
  const {
    insights,
    dismissInsight,
    clearInsights,
    customerProfile,
    closingReadiness
  } = useClosingAssistant();
  const {
    transcript,
    recordingState
  } = useRecording();
  const {
    messages
  } = useChat();
  const {
    setActiveFeature
  } = useAISalesAssistant();
  const [combinedInsights, setCombinedInsights] = useState<any[]>([]);
  // Combine insights from all sources
  useEffect(() => {
    const newInsights = [...insights.map(insight => ({
      ...insight,
      source: 'closing',
      timestamp: insight.timestamp
    }))];
    // Add recent transcript as an insight if available
    if (transcript) {
      newInsights.push({
        id: 'transcript-' + Date.now(),
        type: 'transcript',
        message: transcript,
        severity: 'info',
        timestamp: new Date(),
        source: 'audio',
        dismissed: false
      });
    }
    // Add chat messages as insights
    const chatInsights = messages.filter(msg => msg.sender === 'ai' && msg.products?.length > 0).map(msg => ({
      id: 'chat-' + msg.id,
      type: 'recommendation',
      message: msg.content,
      severity: 'success',
      timestamp: msg.timestamp,
      source: 'chat',
      products: msg.products,
      dismissed: false
    }));
    newInsights.push(...chatInsights);
    // Sort by timestamp, newest first
    setCombinedInsights(newInsights.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()));
  }, [insights, transcript, messages]);
  const activeInsights = combinedInsights.filter(insight => !insight.dismissed);
  if (activeInsights.length === 0) {
    return <div className="p-4 text-center h-full flex flex-col items-center justify-center" data-id="element-221">
        <div className="p-3 rounded-full bg-gray-100 inline-flex items-center justify-center mb-2" data-id="element-222">
          <InfoIcon className="h-6 w-6 text-gray-400" data-id="element-223" />
        </div>
        <p className="text-sm text-gray-500" data-id="element-224">No active insights yet.</p>
        <p className="text-xs text-gray-400 mt-1" data-id="element-225">
          Insights will appear as you interact with the customer.
        </p>
        <div className="mt-6 grid grid-cols-2 gap-3" data-id="element-226">
          <button onClick={() => setActiveFeature('chat')} className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-227">
            <MessageSquareIcon className="h-5 w-5 text-blue-500 mb-1" data-id="element-228" />
            <span className="text-xs text-gray-600" data-id="element-229">Ask AI Assistant</span>
          </button>
          <button onClick={() => setActiveFeature('audio')} className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-230">
            <MicIcon className="h-5 w-5 text-blue-500 mb-1" data-id="element-231" />
            <span className="text-xs text-gray-600" data-id="element-232">Record Conversation</span>
          </button>
        </div>
      </div>;
  }
  // Render readiness score card
  const ReadinessCard = () => <div className="border border-gray-200 rounded-lg p-3 mb-3" data-id="element-233">
      <div className="flex justify-between items-center mb-2" data-id="element-234">
        <div className="flex items-center" data-id="element-235">
          <BrainCircuitIcon className="h-4 w-4 text-gray-500 mr-2" data-id="element-236" />
          <h4 className="text-sm font-medium text-gray-700" data-id="element-237">
            Closing Readiness
          </h4>
        </div>
        <div className="flex items-center" data-id="element-238">
          <span className={`text-sm font-bold ${closingReadiness === 'high' ? 'text-green-600' : closingReadiness === 'medium' ? 'text-yellow-600' : 'text-red-600'}`} data-id="element-239">
            {customerProfile.readinessScore}%
          </span>
          {customerProfile.readinessScore > 50 ? <ArrowUpIcon className="h-3 w-3 text-green-500 ml-1" data-id="element-240" /> : <ArrowDownIcon className="h-3 w-3 text-red-500 ml-1" data-id="element-241" />}
        </div>
      </div>
      <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-242">
        <div className={`h-2 rounded-full ${customerProfile.readinessScore >= 70 ? 'bg-green-500' : customerProfile.readinessScore >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
        width: `${customerProfile.readinessScore}%`
      }} data-id="element-243"></div>
      </div>
      <button onClick={() => setActiveFeature('closing')} className="w-full mt-2 text-xs text-blue-600 hover:underline text-right" data-id="element-244">
        View details →
      </button>
    </div>;
  return <div className="p-3 h-full overflow-y-auto" data-id="element-245">
      <div className="flex justify-between items-center mb-2 px-1" data-id="element-246">
        <h4 className="text-xs font-medium text-gray-500" data-id="element-247">UNIFIED INSIGHTS</h4>
        <button onClick={clearInsights} className="text-xs text-gray-500 hover:text-gray-700" data-id="element-248">
          Clear all
        </button>
      </div>
      <ReadinessCard data-id="element-249" />
      <div className="space-y-2" data-id="element-250">
        {activeInsights.map(insight => {
        // Render different card types based on source
        if (insight.source === 'audio') {
          return <div key={insight.id} className="p-3 rounded-lg border bg-purple-50 border-purple-200" data-id="element-251">
                <div className="flex" data-id="element-252">
                  <div className="mr-2 mt-0.5" data-id="element-253">
                    <MicIcon className="h-4 w-4 text-purple-500" data-id="element-254" />
                  </div>
                  <div className="flex-1" data-id="element-255">
                    <p className="text-sm font-medium text-gray-800" data-id="element-256">
                      Conversation Detected
                    </p>
                    <p className="text-xs text-gray-600 mt-1" data-id="element-257">
                      {insight.message}
                    </p>
                    <div className="flex items-center mt-1" data-id="element-258">
                      <ClockIcon className="h-3 w-3 text-gray-400 mr-1" data-id="element-259" />
                      <span className="text-xs text-gray-500" data-id="element-260">
                        {insight.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                      </span>
                      <span className="ml-2 text-xs px-1.5 py-0.5 bg-white bg-opacity-50 rounded text-gray-600" data-id="element-261">
                        transcript
                      </span>
                    </div>
                  </div>
                  <button onClick={() => dismissInsight(insight.id)} className="ml-2 text-gray-400 hover:text-gray-600" data-id="element-262">
                    <XIcon className="h-3 w-3" data-id="element-263" />
                  </button>
                </div>
              </div>;
        } else if (insight.source === 'chat' && insight.products) {
          return <div key={insight.id} className="p-3 rounded-lg border bg-blue-50 border-blue-200" data-id="element-264">
                <div className="flex" data-id="element-265">
                  <div className="mr-2 mt-0.5" data-id="element-266">
                    <MessageSquareIcon className="h-4 w-4 text-blue-500" data-id="element-267" />
                  </div>
                  <div className="flex-1" data-id="element-268">
                    <p className="text-sm font-medium text-gray-800" data-id="element-269">
                      {insight.message}
                    </p>
                    <div className="mt-2 space-y-2" data-id="element-270">
                      {insight.products.slice(0, 1).map((product: any) => <div key={product.id} className="bg-white rounded-md overflow-hidden shadow-sm border border-gray-200" data-id="element-271">
                          <div className="flex" data-id="element-272">
                            <img src={product.image} alt={product.title} className="h-16 w-16 object-cover" data-id="element-273" />
                            <div className="p-2 flex-1" data-id="element-274">
                              <h4 className="font-medium text-gray-800 text-xs" data-id="element-275">
                                {product.title}
                              </h4>
                              <div className="flex justify-between items-center mt-1" data-id="element-276">
                                <p className="text-xs text-gray-600" data-id="element-277">
                                  {product.destination}
                                </p>
                                <p className="text-xs font-bold text-blue-600" data-id="element-278">
                                  ${product.price}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>)}
                      {insight.products.length > 1 && <button onClick={() => setActiveFeature('chat')} className="text-xs text-blue-600 hover:underline" data-id="element-279">
                          View all {insight.products.length} matches →
                        </button>}
                    </div>
                    <div className="flex items-center mt-1" data-id="element-280">
                      <ClockIcon className="h-3 w-3 text-gray-400 mr-1" data-id="element-281" />
                      <span className="text-xs text-gray-500" data-id="element-282">
                        {insight.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => dismissInsight(insight.id)} className="ml-2 text-gray-400 hover:text-gray-600" data-id="element-283">
                    <XIcon className="h-3 w-3" data-id="element-284" />
                  </button>
                </div>
              </div>;
        } else {
          // Default insight rendering (from closing assistant)
          return <div key={insight.id} className={`p-3 rounded-lg border ${insight.severity === 'success' ? 'bg-green-50 border-green-200' : insight.severity === 'warning' ? 'bg-yellow-50 border-yellow-200' : insight.severity === 'error' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`} data-id="element-285">
                <div className="flex" data-id="element-286">
                  <div className="mr-2 mt-0.5" data-id="element-287">
                    {insight.severity === 'success' && <CheckCircleIcon className="h-4 w-4 text-green-500" data-id="element-288" />}
                    {insight.severity === 'warning' && <AlertCircleIcon className="h-4 w-4 text-yellow-500" data-id="element-289" />}
                    {insight.severity === 'error' && <XCircleIcon className="h-4 w-4 text-red-500" data-id="element-290" />}
                    {insight.severity === 'info' && <InfoIcon className="h-4 w-4 text-blue-500" data-id="element-291" />}
                  </div>
                  <div className="flex-1" data-id="element-292">
                    <p className="text-sm font-medium text-gray-800" data-id="element-293">
                      {insight.message}
                    </p>
                    <div className="flex items-center mt-1" data-id="element-294">
                      <ClockIcon className="h-3 w-3 text-gray-400 mr-1" data-id="element-295" />
                      <span className="text-xs text-gray-500" data-id="element-296">
                        {insight.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                      </span>
                      {insight.category && <span className="ml-2 text-xs px-1.5 py-0.5 bg-white bg-opacity-50 rounded text-gray-600" data-id="element-297">
                          {insight.category}
                        </span>}
                    </div>
                  </div>
                  <button onClick={() => dismissInsight(insight.id)} className="ml-2 text-gray-400 hover:text-gray-600" data-id="element-298">
                    <XIcon className="h-3 w-3" data-id="element-299" />
                  </button>
                </div>
              </div>;
        }
      })}
      </div>
      {/* Recording indicator */}
      {recordingState === 'recording' && <div className="mt-3 p-3 rounded-lg border border-red-200 bg-red-50 flex items-center" data-id="element-300">
          <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse mr-2" data-id="element-301"></div>
          <span className="text-sm text-red-600" data-id="element-302">
            Recording conversation...
          </span>
          <button onClick={() => setActiveFeature('audio')} className="ml-auto text-xs text-red-600 hover:underline" data-id="element-303">
            View →
          </button>
        </div>}
    </div>;
};